<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50">
    <!-- Header section with UP colors -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">Timetable</h1>
            <p class="text-blue-100">Manage all your university schedules in one place</p>
            <div v-if="isAdmin" class="mt-2 bg-yellow-400 text-blue-900 px-3 py-1 rounded-full text-xs font-medium inline-flex items-center">
              <span class="material-symbols-outlined text-sm mr-1">admin_panel_settings</span>
              Admin Mode
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <!-- Timetable Cards Overview -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <timetable-card
          v-for="type in timetableTypes"
          :key="type.id"
          :title="type.title"
          :icon="type.icon"
          :description="type.description"
          :has-data="hasTimetableData(type.id)"
          :data="getPreviewData(type.id)"
          :preview-type="type.previewType || type.id"
          :is-global="isTimetableGlobal(type.id)"
          @view="navigateToPage(type.id)"
        />
      </div>
    </div>

    <!-- FAB for help -->
    <button 
      @click="showHelpSheet = true"
      class="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-blue-900 text-white flex items-center justify-center shadow-lg hover:bg-blue-800 transition-colors"
    >
      <span class="material-symbols-outlined text-2xl">help</span>
    </button>

    <!-- Bottom sheet for mobile actions -->
    <app-bottom-sheet v-model="showHelpSheet" title="Timetable Help" icon="help">
      <div class="p-4">
        <h3 class="font-bold text-lg mb-4">How to use the timetable feature:</h3>
        <ol class="list-decimal pl-5 space-y-2">
          <li>Select the academic tool you want to use</li>
          <li>View your timetables or add new entries</li>
          <li>Open Credits Tracker to review your module credits</li>
        </ol>
        <div class="mt-6">
          <h4 class="font-medium mb-2">Available Timetables:</h4>
          <ul class="space-y-2">
            <li v-for="type in timetableTypes" :key="type.id" class="flex items-center">
              <span class="material-symbols-outlined mr-2">{{ type.icon }}</span>
              {{ type.title }} - {{ type.description }}
            </li>
          </ul>
        </div>
      </div>
    </app-bottom-sheet>
  </div>
  <BottomNav />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import BottomNav from '@/components/common/BottomNav.vue';
import TimetableCard from '@/components/timetable/TimetableCard.vue';
import AppBottomSheet from '@/components/common/AppBottomSheet.vue';
import { mapState, mapGetters } from 'vuex';
import { getUserTimetables } from '@/composables/useStorage';
import { getPersonalTimetable } from '@/composables/usePersonalTimetable';
import { formatCredits, getKnownCreditsCount, getModuleCredits, getTargetCredits } from '@/utils/creditEstimator';

export default {
  components: {
    AppHeader,
    BottomNav,
    TimetableCard,
    AppBottomSheet
  },
  data() {
    return {
      timetables: {
        classes: [],
        semesterTests: [],
        personal: [],
        academicCalendar: []
      },
      showHelpSheet: false,
      isLoading: false,
      globalTimetables: {},
      timetableTypes: [
        {
          id: 'classes',
          title: 'Lectures & Tutorials',
          icon: 'class',
          description: 'Regular weekly lecture schedule and tutorials'
        },
        {
          id: 'semesterTests',
          title: 'Semester Tests',
          icon: 'quiz',
          description: 'Schedule for all your semester tests and assessments'
        },
        {
          id: 'personal',
          title: 'Personal Schedule',
          icon: 'event_note',
          description: 'Your custom events, tasks, and reminders'
        },
        {
          id: 'academicCalendar',
          title: 'Academic Calendar',
          icon: 'calendar_month',
          description: 'University academic calendar and important dates'
        },
        {
          id: 'credits',
          title: 'Credits Tracker',
          icon: 'workspace_premium',
          description: 'Track credits from your selected modules',
          previewType: 'credits'
        }
      ]
    };
  },
  computed: {
    ...mapState(['user', 'profile', 'userClasses', 'semesterTests', 'globalSettings']),
    ...mapGetters(['upcomingSemesterTests']),
    
    isAuthenticated() {
      return this.user && this.user.uid;
    },
    
    isAdmin() {
      return this.profile && this.profile.role === 'admin';
    },

    selectedModules() {
      return Array.isArray(this.profile?.university?.modules)
        ? this.profile.university.modules
        : [];
    },

    moduleCreditsMap() {
      return this.profile?.university?.moduleCredits || {};
    },

    moduleCreditRows() {
      return getModuleCredits(this.selectedModules, this.moduleCreditsMap);
    },

    currentCredits() {
      return this.moduleCreditRows.reduce((sum, module) => (
        module.credits === null ? sum : sum + module.credits
      ), 0);
    },

    currentCreditsDisplay() {
      return this.knownCreditsCount === 0 ? null : this.currentCredits;
    },

    knownCreditsCount() {
      return getKnownCreditsCount(this.selectedModules, this.moduleCreditsMap);
    },

    targetCredits() {
      return getTargetCredits(this.profile, this.globalSettings);
    },
    
    // Get semester tests for the user's modules
    userModuleSemesterTests() {
      // If there's no profile or no modules, return empty array
      if (!this.profile || !this.profile.university || !this.profile.university.modules) {
        return [];
      }
      
      // Extract module codes from the user's profile
      const userModules = this.profile.university.modules.map(module => {
        return typeof module === 'string' ? module : (module.code || '');
      }).filter(Boolean);
      
      // Filter semester tests to only include user's modules
      return this.semesterTests.filter(test => {
        return userModules.some(code => test.moduleCode.startsWith(code));
      });
    }
  },
  watch: {
    'user.uid': {
      immediate: true,
      handler(newUid) {
        if (newUid) {
          this.loadAllTimetables();
          // Also ensure we have semester test data
          this.loadSemesterTestData();
        }
      }
    }
  },
  methods: {
    navigateToPage(type, params = {}) {
      const routes = {
        'classes': '/timetable/classes',
        'semesterTests': '/timetable/tests',
        'personal': '/timetable/personal',
        'academicCalendar': '/timetable/calendar',
        'credits': '/timetable/credits'
      };
      
      const route = routes[type];
      if (!route) return;
      
      // Add any query parameters
      const query = {};
      if (params.action === 'upload') {
        query.upload = 'true';
      }
      if (params.isAdmin) {
        query.admin = 'true';
      }
      
      this.$router.push({ path: route, query });
    },

    async loadAllTimetables() {
      if (!this.isAuthenticated) return;
      
      this.isLoading = true;
      
      try {
        const result = await getUserTimetables(this.user.uid, this.profile);
        
        if (result.success) {
          this.timetables = {
            classes: [],
            semesterTests: [],
            personal: []
          };
          
          const timetableData = result.timetables;
          
          for (const type in timetableData) {
            if (Object.prototype.hasOwnProperty.call(this.timetables, type)) {
              if (timetableData[type].processedData) {
                this.timetables[type] = timetableData[type].processedData;
                
                if (timetableData[type].isGlobal) {
                  this.timetables[type].isGlobal = true;
                }
              } else if (timetableData[type]) {
                this.timetables[type] = timetableData[type];
              }
            }
          }
          
          this.globalTimetablesAvailable = result.globalTimetablesAvailable;
        } else {
          console.error("Failed to load timetables:", result.message);
        }

        // Load personal timetable separately
        const personalResult = await getPersonalTimetable(this.user.uid);
        if (personalResult.success) {
          this.timetables.personal = personalResult.entries;
        }
      } catch (error) {
        console.error("Error loading timetables:", error);
        this.$store.dispatch('showAlert', { 
          message: `Failed to load your timetables: ${error.message}`, 
          type: "error" 
        });
      } finally {
        this.isLoading = false;
      }
    },
    
    getPreviewData(type) {
      if (type === 'classes') {
        if (!this.userClasses || this.userClasses.length === 0) {
          return [];
        }
        
        return this.userClasses.slice(0, 2).map(classItem => ({
          Module: classItem.moduleCode,
          Day: classItem.day,
          Time: `${classItem.startTime} - ${classItem.endTime}`,
          Venue: classItem.venue,
          Type: classItem.type
        }));
      }
      
      // For personal timetable, show upcoming entries
      if (type === 'personal') {
        if (!this.timetables.personal || this.timetables.personal.length === 0) {
          return [];
        }
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        return this.timetables.personal
          .filter(entry => {
            const entryDate = new Date(entry.date);
            return entryDate >= today;
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 2)
          .map(entry => ({
            Title: entry.title,
            Date: entry.date,
            Time: `${entry.startTime} - ${entry.endTime}`,
            Type: entry.type,
            Location: entry.location || '-'
          }));
      }
      
      // For semester tests, use the cached data from the store and remove duplicates
      if (type === 'semesterTests') {
        // Remove duplicates first
        const uniqueTests = new Map();
        
        for (const test of this.userModuleSemesterTests) {
          const key = `${test.moduleCode}-${test.test}-${test.date}-${test.time}`;
          if (!uniqueTests.has(key)) {
            uniqueTests.set(key, test);
          }
        }
        
        const upcomingTests = Array.from(uniqueTests.values())
          .filter(test => {
            const testDate = this.parseTestDate(test.date);
            return testDate >= new Date();
          })
          .sort((a, b) => {
            const dateA = this.parseTestDate(a.date);
            const dateB = this.parseTestDate(b.date);
            return dateA - dateB;
          })
          .slice(0, 2); // Only take first 2 tests
          
        return upcomingTests.map(test => ({
          Module: test.moduleCode,
          Test: test.test,
          Date: test.date,
          Time: test.time,
          Venue: test.venue,
          Campus: test.campus
        }));
      }
      
      // For academic calendar, show upcoming events
      if (type === 'academicCalendar') {
        if (!this.timetables.academicCalendar || this.timetables.academicCalendar.length === 0) {
          return [];
        }
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        return this.timetables.academicCalendar
          .filter(event => {
            const eventDate = new Date(event.isRange ? event.startDate : event.date);
            return eventDate >= today;
          })
          .sort((a, b) => {
            const dateA = new Date(a.isRange ? a.startDate : a.date);
            const dateB = new Date(b.isRange ? b.startDate : b.date);
            return dateA - dateB;
          })
          .slice(0, 2)
          .map(event => ({
            Title: event.title,
            Date: event.isRange ? `${event.startDate} to ${event.endDate}` : event.date,
            Category: event.category.replace('_', ' '),
            Details: event.details || '-'
          }));
      }

      if (type === 'credits') {
        return [
          {
            label: 'Known Credits',
            value: formatCredits(this.currentCreditsDisplay)
          },
          {
            label: 'Target Credits',
            value: formatCredits(this.targetCredits)
          },
          {
            label: 'Credit Data',
            value: `${this.knownCreditsCount} of ${this.selectedModules.length} ${this.selectedModules.length === 1 ? 'module' : 'modules'} set`
          }
        ];
      }
      
      if (!this.timetables[type] || !Array.isArray(this.timetables[type])) {
        return [];
      }
      
      return this.timetables[type].slice(0, 2);
    },
    
    hasTimetableData(type) {
      if (type === 'classes') {
        return this.userClasses && this.userClasses.length > 0;
      }
      
      // For personal timetable, check local data
      if (type === 'personal') {
        return this.timetables.personal && this.timetables.personal.length > 0;
      }
      
      // For semester tests, check the store data
      if (type === 'semesterTests') {
        return this.userModuleSemesterTests && this.userModuleSemesterTests.length > 0;
      }
      
      // For academic calendar, check if data exists
      if (type === 'academicCalendar') {
        return this.timetables.academicCalendar && this.timetables.academicCalendar.length > 0;
      }

      if (type === 'credits') {
        return this.selectedModules.length > 0;
      }
      
      return Array.isArray(this.timetables[type]) && this.timetables[type].length > 0;
    },
    
    isTimetableGlobal(type) {
      if (this.isAdmin) return false;
      
      // Academic calendar is always global
      if (type === 'academicCalendar') {
        return true;
      }
      
      // Personal timetable is never global
      if (type === 'classes' || type === 'personal') {
        return false;
      }
      
      if (!this.timetables[type]) return false;
      
      if (Array.isArray(this.timetables[type])) {
        return this.timetables[type].isGlobal === true;
      }
      
      return this.timetables[type].isGlobal === true;
    },
    
    // Helper function to parse test dates
    parseTestDate(dateStr) {
      // Parse dates in format "28 Mar 2025"
      const [day, month, year] = dateStr.split(' ');
      
      const months = {
        'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
        'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
      };
      
      return new Date(parseInt(year), months[month], parseInt(day));
    },
    
    // Method to load semester test data if it's not already loaded
    async loadSemesterTestData() {
      if (this.profile && this.profile.university && 
          this.profile.university.modules && 
          this.profile.university.modules.length > 0) {
            
        // Extract module codes
        const moduleCodes = this.profile.university.modules.map(module => {
          return typeof module === 'string' ? module : (module.code || '');
        }).filter(Boolean);
        
        // Load tests for these modules using the store action
        if (moduleCodes.length > 0) {
          await this.$store.dispatch('fetchSemesterTests', { moduleCodes });
        }
      }
    }
  }
};
</script>

<style scoped>
@keyframes pulse {
  0%, 100% {
    width: 25%;
  }
  50% {
    width: 75%;
  }
}

.animate-pulse {
  animation: pulse 1.5s ease-in-out infinite;
}
</style>
